import Cropper from 'react-cropper'
import 'cropperjs/dist/cropper.min.css'
import { forwardRef, ReactElement, Ref, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import CropIcon from '@mui/icons-material/Crop'
import { Grid, IconButton, Dialog, Slide, AppBar, Toolbar, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { TransitionProps } from '@mui/material/transitions'
import { CropImageProps, Image } from './type'
import canvasToBlob from '../../utils/image/canvasToBlob'
import resizerImage from '../../utils/image/resizerImage'

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement
  },
  ref: Ref<unknown>,
) {
  return <Slide direction='up' ref={ref} {...props} />
})

const CropImage = ({
  source,
  open,
  handleClose,
  initialAspectRatio,
  aspectRatio,
}: CropImageProps): JSX.Element => {
  const { setValue, getValues } = useFormContext()
  const [cropper, setCropper] = useState<Cropper>()
  const record = getValues(source)
  if (record == null) {
    return <></>
  }
  const { src: path } = record
  const getCropData = async (): Promise<void> => {
    if (typeof cropper !== 'undefined') {
      const fileCropInfo = cropper.getCanvasData()
      const cropImg = await canvasToBlob(cropper.getCroppedCanvas())
      const blobToFile = new File([cropImg], record.title, {
        type: cropImg.type,
        lastModified: new Date().getTime(),
      })
      const rawFile = await resizerImage(blobToFile, fileCropInfo.width, fileCropInfo.height)
      const preview = URL.createObjectURL(rawFile)
      const transformedFile: Image = {
        rawFile: rawFile,
        src: preview,
        title: record.title,
      }
      setValue(source, transformedFile, { shouldDirty: true })
      handleClose()
    }
  }
  return (
    <Dialog fullScreen open={open} onClose={handleClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <Typography sx={{ ml: 2, flex: 1 }} variant='h6' component='div'>
            Crop Image
          </Typography>
          <IconButton autoFocus color='inherit' onClick={getCropData} aria-label='crop image'>
            <CropIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box sx={{ pt: '20px' }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Cropper
              style={{ height: '80%', width: '100%' }}
              zoomTo={0.5}
              zoomable={false}
              initialAspectRatio={initialAspectRatio}
              aspectRatio={aspectRatio}
              src={path}
              viewMode={1}
              minCropBoxHeight={250}
              minCropBoxWidth={250}
              background={false}
              responsive={true}
              autoCropArea={1}
              checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
              onInitialized={(instance: Cropper): void => {
                setCropper(instance)
              }}
              guides={true}
            />
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  )
}

export default CropImage
